import _ from "lodash";
import clsx from "clsx";

import useAccountMetadata from "@/hooks/useAccountMetadata";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { EditableProject } from "@/reducers/environments";

type UserEnvironmentSelectorBaseProps = {
  getHasAccess: (projID: string, envID: string) => boolean;
  updateAccess: (
    projID: string | null,
    envID: string | null,
    hasAccess: boolean,
  ) => void;
  enableToggle: boolean;
  showAllProjectsEnvs: boolean;
  showProjectCheckbox: boolean;
};

export const UserEnvironmentSelectorBase = ({
  getHasAccess,
  updateAccess,
  enableToggle,
  showAllProjectsEnvs,
  showProjectCheckbox,
}: UserEnvironmentSelectorBaseProps) => {
  const accountMetadata = useAccountMetadata();

  const projects = accountMetadata.projects.filter(
    (project): project is EditableProject & { ID: string } =>
      project.environments.length > 0 && project.ID !== null,
  );

  const hasAccessProjAllEnvs: Record<string, boolean> = {};
  for (let project of projects) {
    hasAccessProjAllEnvs[project.ID] = _.every(project.environments, (env) =>
      env.ID !== null ? getHasAccess(project.ID, env.ID) : false,
    );
  }
  const hasAccessAllProjsAndEnvs = _.every(hasAccessProjAllEnvs);

  return (
    <div className="text-sm">
      {showAllProjectsEnvs && (
        <div className="flex items-center justify-end gap-4">
          <span className="italic">All Projects and Environments</span>
          <span>
            <Checkbox
              checked={hasAccessAllProjsAndEnvs}
              disabled={!enableToggle}
              onCheckedChange={() => {
                updateAccess(null, null, !hasAccessAllProjsAndEnvs);
              }}
            />
          </span>
        </div>
      )}
      {projects.map((project) => {
        const hasAccessAllEnvs = hasAccessProjAllEnvs[project.ID];

        return (
          <div
            className="flex flex-col items-end"
            key={`user-env-selector-base-project-${project.ID}`}
          >
            <div className="flex items-center justify-end gap-4">
              <span className="font-bold">{project.name}</span>
              <span>
                <Checkbox
                  className={clsx(!showProjectCheckbox && "hidden")}
                  checked={hasAccessAllEnvs}
                  disabled={!enableToggle}
                  onCheckedChange={() => {
                    updateAccess(project.ID, null, !hasAccessAllEnvs);
                  }}
                />
              </span>
            </div>
            {_.sortBy(project.environments, "createdAt").map((environment) => {
              const hasAccess =
                environment.ID !== null &&
                getHasAccess(project.ID, environment.ID);

              return (
                <div
                  className="flex items-center justify-end gap-4"
                  key={`user-env-selector-base-env-${environment.ID}`}
                >
                  <span>{environment.name}</span>
                  <span>
                    <Checkbox
                      checked={hasAccess}
                      disabled={!enableToggle}
                      onCheckedChange={() => {
                        updateAccess(project.ID, environment.ID, !hasAccess);
                      }}
                    />
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
